import React, { useMemo, useRef } from 'react';

import classnames from 'classnames';

import useTranslation from '../../../features/locale/useTranslation';
import { SORTING_MODES } from '../../../utils/list';
import { Col, type ColumnRange } from '../../Grid/Col/Col';
import { Row } from '../../Grid/Row/Row';
import { Icon } from '../../Icon/Icon';
import { Tooltip } from '../../Tooltip/Tooltip';
import useHeaderOffset from '../hooks/useHeaderOffset';
import { VirtualizedList } from '../VirtualizedList/VirtualizedList';

import { ListRow } from './ListRow/ListRow';

import styles from './ListCore.module.scss';

export type ListCoreProps = {
    columns: Array<{
        className?: string;
        component?: (args: unknown) => React.ReactNode;
        flex?: string;
        key?: string;
        onSort?: () => void;
        size?: { xs: number; sm: number; md: number; lg: number; xl: number };
        sortable?: boolean;
        span?: ColumnRange;
        title?: string;
        tooltip?: string | null;
    }>;
    headerClassName?: string;
    height: number;
    highlightEnabled?: boolean;
    includeListHeader?: boolean;
    includeOffset?: boolean;
    includeShowMore?: boolean;
    includeShowTop?: boolean;
    includeTopBorder?: boolean;
    includeBottomBorder?: boolean;
    listClassName?: string;
    overscan?: number;
    rowClassName?: string;
    rowRenderer?: any;
    rows: Array<unknown>;
    scrollToId?: {
        timestamp: number;
        id: string;
    };
    setScrollToId?: React.Dispatch<
        React.SetStateAction<{
            timestamp: number;
            id: string;
            highlight?: boolean;
        }>
    >;
    sortKey?: string;
    sortOrder?: string;
};

export const ListCore = ({
    columns,
    headerClassName,
    height,
    highlightEnabled = true,
    includeListHeader = true,
    includeOffset = true,
    includeShowMore = true,
    includeShowTop = false,
    includeTopBorder = true,
    includeBottomBorder = true,
    listClassName,
    overscan,
    rowClassName,
    rowRenderer = ListRow,
    rows,
    scrollToId,
    setScrollToId,
    sortKey,
    sortOrder
}: ListCoreProps) => {
    const { currentLanguage } = useTranslation();
    const listRef = useRef<HTMLDivElement>(null);
    const offset = useHeaderOffset({ ref: listRef });

    const memoizedHeader = useMemo(
        () => (
            <Row
                className={headerClassName}
                style={{
                    paddingRight: includeOffset ? `${offset + 16}px` : undefined
                }}
            >
                {/* eslint-disable @typescript-eslint/no-unused-vars */}
                {columns.map(
                    (
                        {
                            key,
                            className,
                            title,
                            component: _component,
                            sortable,
                            onSort,
                            tooltip,
                            size,
                            ...rest
                        },
                        idx
                    ) => (
                        <Col
                            key={key ?? idx}
                            className={classnames(className, {
                                [styles.sortable]: sortable,
                                [styles.active]: sortKey === key
                            })}
                            onClick={sortable ? onSort : undefined}
                            {...size}
                            {...rest}
                            data-col-idx={idx}
                            role="columnheader"
                        >
                            {tooltip ? <Tooltip title={tooltip}>{title}</Tooltip> : title}
                            {sortable && (
                                <Icon
                                    className={styles.icon}
                                    aria-label={title}
                                    name={
                                        sortOrder === SORTING_MODES.REVERSE && sortKey === key
                                            ? 's-icon-arrow-up'
                                            : 's-icon-arrow-down'
                                    }
                                />
                            )}
                        </Col>
                    )
                )}
            </Row>
        ),
        [columns, currentLanguage, offset]
    );

    return (
        <>
            <div id="dynamicPreHeaderContent" />
            {includeListHeader && memoizedHeader}
            <div id="dynamicListHeaderContent" />
            <VirtualizedList
                rowClassName={rowClassName}
                columns={columns}
                height={height}
                highlightEnabled={highlightEnabled}
                includeShowMore={includeShowMore}
                includeShowTop={includeShowTop}
                includeTopBorder={includeTopBorder}
                includeBottomBorder={includeBottomBorder}
                listClassName={listClassName}
                overscan={overscan}
                ref={listRef}
                rowRenderer={rowRenderer}
                rows={rows}
                scrollToId={scrollToId}
                setScrollToId={setScrollToId}
            />
        </>
    );
};
